// Other pickers that could use this: create-ta-details secondary approver, manage-project-modal approvers, manage-unit-modal matchUsers, manage-approvers-modal, clc-auditlog-admin user filter.

/**
 * Lowercase, strip diacritics and apostrophes, and turn other punctuation into single spaces,
 * so "Ti-Tree" matches "ti tree" and "Mt Liebig" matches "Mt. Liebig".
 */
function normalise(text: string): string {
  return text.toLowerCase()
    .normalize('NFD').replace(/\p{M}/gu, '')
    .replace(/['‘’`]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * Fewest edits (insert, delete, substitute, swap adjacent) turning the query into any substring
 * of the text. Sellers' algorithm: row 0 is all zeros so the match may start anywhere.
 */
function substringDistance(query: string, text: string): number {
  const d: number[][] = [new Array(text.length + 1).fill(0)];
  for (let i = 1; i <= query.length; i++) {
    d[i] = [i];
    for (let j = 1; j <= text.length; j++) {
      const cost = query[i - 1] === text[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && query[i - 1] === text[j - 2] && query[i - 2] === text[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return Math.min(...d[query.length]);
}

/**
 * Items whose text contains the search term within a few edits (0 for 1-2 letters, 1 for 3-4,
 * 2 for 5+). Text starting with the term scores -1 so it ranks first; then fewest edits, then A–Z.
 * "alice" → Alice Springs before Lady Alice Camp; "tenant" → Tenant Ck, then Tennant Creek.
 * A blank term returns a copy of the list in its original order. The input is not mutated.
 *
 * @param text the property holding each item's text, or a function returning it; omit for a list of strings.
 */
export function fuzzySearch<T>(
  list: readonly T[],
  searchTerm: string,
  text?: keyof T | ((item: T) => string),
): T[] {
  const query = normalise(searchTerm ?? '');
  if (!query) {
    return [...list];
  }
  const maxEdits = query.length < 3 ? 0 : query.length < 5 ? 1 : 2;
  const textOf = (item: T): string =>
    String((typeof text === 'function' ? text(item) : text !== undefined ? item[text] : item) ?? '');

  return list
    .map(item => ({item, text: textOf(item)}))
    .map(r => {
      const normalised = normalise(r.text);
      return {...r, score: normalised.startsWith(query) ? -1 : substringDistance(query, normalised)};
    })
    .filter(r => r.score <= maxEdits)
    .sort((a, b) => a.score - b.score || a.text.localeCompare(b.text))
    .map(r => r.item);
}

type Field = string | null | undefined;

/**
 * Row predicate for "Type to filter…" boxes. Every space-separated word must appear in some field,
 * spaces ignored ("aba201" → ABA-201). Failing that, fuzzy fields may match within a few edits
 * (0 for 1-2 letters, 1 for 3-7, 2 for 8+): "darwn" → Darwin. A blank term matches everything.
 */
export function fuzzyMatcher(searchTerm: string): (exact: readonly Field[], fuzzy: readonly Field[]) => boolean {
  const tokens = normalise(searchTerm ?? '').split(' ').filter(Boolean);
  if (!tokens.length) {
    return () => true;
  }
  const contains = (token: string, field: string) => field.includes(token) || field.replaceAll(' ', '').includes(token);
  const maxEdits = (token: string) => token.length < 3 ? 0 : token.length < 8 ? 1 : 2;

  return (exact, fuzzy) => {
    const exactTexts = exact.map(f => normalise(f ?? ''));
    const fuzzyTexts = fuzzy.map(f => normalise(f ?? ''));
    return tokens.every(token =>
      [...exactTexts, ...fuzzyTexts].some(field => contains(token, field))
      || (maxEdits(token) > 0 && fuzzyTexts.some(field => substringDistance(token, field) <= maxEdits(token))));
  };
}
